import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import PaymentCardItem from 'components/Payment/PaymentCardItem';
import { Desktop, Mobile } from 'components/Responsive';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { PaymentCard } from 'models/paymentCard';
import PaymentAddMethodContainer from 'screens/containers/PaymentAddMethod';
import { saveGroupsCard, removeGroupsCard } from 'services/paymentMethodService';

import PaymentCardNoCard from '../PaymentCardNoCard';
import {
  StyledButton,
  ButtonContainer,
  ButtonGhost,
  BoxContainer,
  SectionContainer,
  Content,
  CreateCardIcon,
} from './styles';

interface Props {
  showCardSelection?: boolean;
  groupId?: string;
  cardList: PaymentCard[];
  isLoading: boolean;
  cardSelected?: PaymentCard;
  onCloseSelectionScreen: () => void;
  onConfirmSelection: (cardId?: string) => void;
}

const PaymentGroupCardSelection = ({
  groupId,
  cardList,
  isLoading,
  onCloseSelectionScreen,
  onConfirmSelection,
  cardSelected,
  showCardSelection,
}: Props) => {
  const [cardResult, setCardResult] = useState<PaymentCard[]>([]);
  const [cardDefault, setCardDefault] = useState<PaymentCard | undefined>(undefined);
  const [selectedCard, setSelectedCard] = useState<PaymentCard | undefined>(cardSelected);
  const [showCard, setShowCard] = useState(false);
  const history = useHistory();
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useStoreTranslation();
  const isMobile = useIsMobile();
  const toast = useToast();

  const handleSelection = (card: PaymentCard) => {
    setSelectedCard(card);
  };

  const handleSubmitSelection = useCallback(async () => {
    setIsSaving(true);
    if (typeof selectedCard !== 'undefined' && typeof groupId !== 'undefined') {
      try {
        if (selectedCard.cardId === cardDefault?.cardId) {
          await removeGroupsCard(selectedCard.cardId, [groupId]);
          setIsSaving(false);
          onConfirmSelection(selectedCard.cardId);
          onCloseSelectionScreen();
        } else {
          await saveGroupsCard(selectedCard.cardId, [groupId]);
          setIsSaving(false);
          onConfirmSelection(selectedCard.cardId);
          onCloseSelectionScreen();
        }
      } catch (error) {
        toast.add({
          type: 'danger',
          message: error.message,
        });
        setIsSaving(false);
      }
    } else {
      onConfirmSelection(selectedCard?.id);
      onCloseSelectionScreen();
    }
  }, [selectedCard, groupId, toast, cardDefault, onCloseSelectionScreen, onConfirmSelection]);

  const handleNavigateHistory = () => {
    if (groupId) {
      history.push(`/corporate/group/${groupId}`);
      return;
    }
    onCloseSelectionScreen();
  };

  const renderAddCard = () => (
    <ApplicationOverlayContent sidebar show={showCard} onClose={() => setShowCard(false)}>
      <PaymentAddMethodContainer
        match={{ params: { groupId: groupId ? groupId : '' } }}
        onCloseModal={() => {
          setShowCard(false);
        }}
        onAdded={(cardId?: string) => {
          setShowCard(false);
          onConfirmSelection(cardId);
          onCloseSelectionScreen();
        }}
      />
    </ApplicationOverlayContent>
  );

  useEffect(() => {
    const cardListFiltered = cardList.filter((card) => !card.defaultCC);
    const cardDefaultList = cardList.filter((card) => card.defaultCC);
    setCardDefault(cardDefaultList[0]);
    setCardResult(cardListFiltered);
    if (cardSelected) setSelectedCard(cardSelected);
  }, [cardList, cardSelected]);

  return (
    <>
      <Mobile>
        <AppBar goBack={handleNavigateHistory}>
          <h5>{t('payment.cardList.cardListTitle')}</h5>
        </AppBar>
      </Mobile>

      {isLoading ? (
        <Loader fullScreen={isMobile} />
      ) : (
        <>
          <Desktop>
            <ModalHeader title={t('payment.cardList.cardListTitle')} onClose={handleNavigateHistory} />
          </Desktop>
          {cardList.length === 0 ? (
            <>
              {renderAddCard()}
              <PaymentCardNoCard
                onClick={() => {
                  if (groupId) {
                    history.push(`/corporate/group/${groupId}/payment/add`);
                    return;
                  }
                  setShowCard(true);
                }}
              />
            </>
          ) : (
            <SectionContainer>
              <BoxContainer>
                <ButtonGhost
                  onClick={() => {
                    if (groupId) {
                      history.push(`/corporate/group/${groupId}/payment/add`);
                      return;
                    }
                    setShowCard(true);
                  }}
                >
                  <CreateCardIcon />
                  {t('payment.cardList.btnAddCard')}
                </ButtonGhost>
              </BoxContainer>
              {renderAddCard()}
              {typeof cardDefault !== 'undefined' ? (
                <Content>
                  <h3>{t('payment.cardList.default.title')}</h3>
                  <p>{t('payment.cardList.default.subtitle')}</p>
                  <PaymentCardItem
                    card={cardDefault}
                    onClick={() => handleSelection(cardDefault)}
                    isSelectable={true}
                    isSelected={selectedCard?.id === cardDefault.id}
                  />
                </Content>
              ) : null}
              {cardResult.length === 0 ? null : (
                <Content className="others">
                  <h3>{t('payment.cardList.otherCard')}</h3>
                  {cardResult.map((card, idx) => (
                    <PaymentCardItem
                      key={idx}
                      card={card}
                      onClick={() => handleSelection(card)}
                      isSelectable={true}
                      isSelected={selectedCard?.id === card.id}
                    />
                  ))}
                </Content>
              )}
              <ButtonContainer>
                <StyledButton
                  onClick={handleSubmitSelection}
                  disabled={(selectedCard !== null && selectedCard?.id === cardSelected?.id) || isSaving}
                  loading={isSaving}
                >
                  {t('payment.cardList.btnSelection')}
                </StyledButton>
              </ButtonContainer>
            </SectionContainer>
          )}
        </>
      )}
    </>
  );
};

export default PaymentGroupCardSelection;
